import React from 'react';
import ReactDOM from 'react-dom';
import { connect } from 'react-redux';
import classnames from 'classnames';
import './golden-layout-jquery';

import GoldenLayout, {
    ContentItem,
    ItemConfig,
    ReactComponentConfig,
} from 'golden-layout';
import LayoutShape, {
    areLayoutShapesEqual,
    LayoutContainerShape,
} from 'layout/LayoutShape';
import LayoutRendererComponent, {
    fromLayoutShape,
    LayoutRendererReactComponent,
} from './LayoutRendererComponent';
import createRootComponentClass from './RootComponent';
import { selectLayoutRenderer } from './store/selectors';
import { layoutRendererDispatcher } from './store/dispatcher';
import 'golden-layout/src/css/goldenlayout-base.css';
import 'golden-layout/src/css/goldenlayout-dark-theme.css';
import './LayoutRenderer.scss';
import { WidgetsService } from 'widgets/WidgetsService';
import Services from 'store/Services';
import { WidgetId } from 'widgets/WidgetId';
import {
    addEditTitleButton,
    editingTitle,
    isTargetEditTitleButton,
} from 'layoutRenderer/EditTitleButton';
import { PendingWidgetToAdd } from 'layoutRenderer/LayoutRendererState';
import { AnyConfig } from 'widgets/WidgetRegistry';
import { Store } from 'redux';
import { WidgetType } from 'widgets/WidgetType';
import { CapsuleContainerWidgetConfigState } from 'widgets/tiles/store/config/CapsuleContainerWidgetConfigState';
import { makeCapsuleIdsUnique } from 'widgets/configSharing/shareWidgetConfigModal/ShareWidgetConfigModal';
import {
    configFromProps,
    ensureTopItemIsRow,
    findFirstActiveInput,
    hasCurrentLayoutChanged,
    isDragging,
    LayoutRendererConfig,
    shapefromConfig,
} from './utils';
import { AddLayoutDragSourcePubSub } from '../appToolbar/AddLayoutDragSourcePubSub';
import { LayoutRendererDragDropManager } from './LayoutRendererDragDropManager';

/* eslint-disable @typescript-eslint/no-explicit-any */

(window as any).React = (window as any).React || React;
(window as any).ReactDOM = (window as any).ReactDOM || ReactDOM;

interface LayoutRendererProps {
    layoutId: number | null;
    layoutShape: LayoutShape | undefined;
    saveLayoutShape: (layoutShape: LayoutShape | undefined) => void;
    locked: boolean;
    pendingWidgetsToAdd: PendingWidgetToAdd[];
    confirmAddWidgets: (
        widgets: Array<{ id: WidgetId; config?: AnyConfig }>
    ) => void;
    onDraggingWidget: (added: { id: WidgetId; config?: AnyConfig }) => void;
    onDraggedWidgetComplete: (added: { id: WidgetId }) => void;
    onGlStateChanged: () => void;
    addLayoutDragSourcePubSub: AddLayoutDragSourcePubSub;
    width: number;
    height: number;
    services: Services;
    store: Store;
}

class LayoutRenderer extends React.Component<LayoutRendererProps> {
    private ref?: HTMLDivElement;
    private gl?: GoldenLayout;
    private saveConfigTimeout?: NodeJS.Timeout;
    private widgetsService: WidgetsService;
    private services: Services;
    private onGlStateChanged: () => void;
    private addLayoutDragSourcePubSub: AddLayoutDragSourcePubSub;
    private store: Store;
    private dragDropManager: LayoutRendererDragDropManager | undefined;

    public constructor(props: LayoutRendererProps) {
        super(props);
        this.ref = undefined;
        this.gl = undefined;
        this.services = props.services;
        this.widgetsService = props.services.widgetsService;
        this.onGlStateChanged = props.onGlStateChanged;
        this.addLayoutDragSourcePubSub = props.addLayoutDragSourcePubSub;
        this.store = props.store;
    }

    public async componentDidUpdate(prevProps: LayoutRendererProps) {
        if (!this.gl) {
            return;
        }

        if (
            hasCurrentLayoutChanged(prevProps.layoutId, this.props.layoutId) ||
            (prevProps.layoutShape === undefined &&
                this.props.layoutShape !== undefined)
        ) {
            const config = this.configFromProps();
            this.initLayout(config);
            return;
        }

        const prevContentsLength =
            (prevProps.layoutShape as LayoutContainerShape)?.content?.length ??
            0;
        const nextContentsLength =
            (this.props.layoutShape as LayoutContainerShape)?.content?.length ??
            0;

        if (prevContentsLength === 0 && nextContentsLength > 0) {
            this.handleResize();
        }

        if (
            prevProps.width !== this.props.width ||
            prevProps.height !== this.props.height
        ) {
            this.handleResize();
        }

        const { pendingWidgetsToAdd, confirmAddWidgets, services } = this.props;
        const prevPendingWidgetsToAdd = prevProps.pendingWidgetsToAdd;

        if (
            prevPendingWidgetsToAdd.length === 0 &&
            pendingWidgetsToAdd.length > 0
        ) {
            const topLevelItem = await ensureTopItemIsRow(this.gl!);

            const added: Array<{ id: WidgetId; config?: AnyConfig }> = [];
            const componentsToAdd: LayoutRendererComponent[] = [];

            for (const pendingWidgetToAdd of pendingWidgetsToAdd) {
                const componentId =
                    this.props.services.widgetsService.getNextWidgetId(
                        pendingWidgetToAdd.widgetType
                    );

                const item = fromLayoutShape(
                    {
                        type: 'component',
                        componentType: pendingWidgetToAdd.widgetType,
                        componentId,
                    },
                    this.store,
                    services
                ) as LayoutRendererReactComponent;
                componentsToAdd.push(item);

                this.onAddingComponent(
                    pendingWidgetToAdd.config,
                    pendingWidgetToAdd.widgetType
                );

                added.push({
                    id: {
                        widgetType: pendingWidgetToAdd.widgetType,
                        widgetKey: componentId,
                    },
                    config: pendingWidgetToAdd.config,
                });
            }

            confirmAddWidgets(added);

            await new Promise<void>((resolve) =>
                setTimeout(() => {
                    for (const componentToAdd of componentsToAdd) {
                        topLevelItem.addChild(componentToAdd);
                    }

                    resolve();
                }, 0)
            );
        }
    }

    private registerReactComponents() {
        this.widgetsService.getReactComponents().forEach((w) => {
            this.gl!.registerComponent(
                w.type,
                createRootComponentClass(w.reactComponentType)
            );
        });
    }

    private onStateChanged = () => {
        if (isDragging()) {
            return;
        }

        if (this.dragDropManager) {
            this.dragDropManager?.onDragComplete();
        }

        this.onGlStateChanged();

        (this.gl! as any).activeInput = findFirstActiveInput(
            this.gl!.root.contentItems[0],
            this.widgetsService
        );

        if (this.saveConfigTimeout) {
            clearTimeout(this.saveConfigTimeout);
        }
        this.saveConfigTimeout = setTimeout(() => {
            const { layoutShape, saveLayoutShape } = this.props;
            const config = this.gl!.toConfig();

            if (!config) {
                return;
            }
            const layoutShapeToSave = shapefromConfig(config!);
            if (areLayoutShapesEqual(layoutShapeToSave, layoutShape)) {
                return;
            }
            saveLayoutShape(layoutShapeToSave);
        }, 0);
    };

    private initLayout(config: GoldenLayout.Config) {
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        const self = this;

        setTimeout(() => {
            if (this.ref) {
                this.ref.innerHTML = '';
            }
            if (this.gl) {
                this.gl.destroy();
                this.gl = undefined;
            }

            const gl = (this.gl = new GoldenLayout(config, this.ref));
            this.registerReactComponents();
            gl.on('stateChanged', this.onStateChanged);
            gl.on('tabCreated', this.onTabCreated);
            gl.on('itemCreated', this.onItemCreated);
            gl.init();

            if (this.dragDropManager) {
                this.dragDropManager.updateGl(gl);
            } else {
                this.dragDropManager = new LayoutRendererDragDropManager({
                    gl,
                    addLayoutDragSourcePubSub: this.addLayoutDragSourcePubSub,
                    onDraggingWidget: self.onDraggingWidget.bind(self),
                    onDraggedWidgetComplete: this.props.onDraggedWidgetComplete,
                    store: this.store,
                    services: this.services,
                });
            }

            this.handleResize();
        }, 0);
    }

    private onItemCreated = (item: GoldenLayout.ContentItem) => {
        if (item.type === 'stack') {
            item.on(
                'activeContentItemChanged',
                this.onStackActiveContentItemChanged.bind(this)
            );
        }
        if (item.type === 'component') {
            const config = item.config as ReactComponentConfig;
            const widgetType = config.props.componentType;

            if (this.dragDropManager) {
                // This may bump the id...
                this.dragDropManager.onItemCreated(item);
            }

            const widgetId = config.props.componentId;
            const element = (item.element as any)[0];
            if (element && widgetId) {
                element.setAttribute('data-component-type', widgetType);
                element.setAttribute('data-component-id', widgetId);
            }
        }
    };

    private onStackActiveContentItemChanged(item: ContentItem) {
        const itemConfig = item.config as LayoutRendererReactComponent;
        if (!itemConfig.props?.componentId) {
            return;
        }

        const itemId = itemConfig.props.componentId;

        const parentConfig = item.parent?.config as ItemConfig & {
            activeItemIndex: number;
        };

        const parentItems = parentConfig?.content ?? [];

        const itemConfigInStack = parentItems.find(
            (a) =>
                (a as LayoutRendererReactComponent).props.componentId === itemId
        );

        if (!itemConfigInStack) {
            return;
        }

        const itemIndexInStack = parentItems.indexOf(itemConfigInStack);

        if (parentConfig.activeItemIndex !== itemIndexInStack) {
            // HACK to fix https://jira.isn.instinet.com:8443/browse/IND2-2669, where golden layout
            // does not update the activeItemIndex when tabs are hidden in the drop-down due to lack of width
            parentConfig.activeItemIndex = itemIndexInStack;
        }
    }

    private onTabCreated = (
        tab: GoldenLayout.Tab & {
            _dragListener: {
                _fDown: (e: { preventDefault: () => void }) => void;
            };
        }
    ) => {
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        const self = this;

        addEditTitleButton(tab, this.store, this.services);

        (tab.element[0] as HTMLElement).setAttribute(
            'data-tab-widget-key',
            (tab.contentItem.config as LayoutRendererReactComponent).props
                .componentId
        );

        const defaultFDown = tab._dragListener._fDown;
        tab.element
            .off('mousedown touchstart')
            .on('mousedown touchstart' as any, function (e: React.MouseEvent) {
                if (isTargetEditTitleButton(e)) {
                    // Clicking edit title button...
                    return;
                }
                if (editingTitle(tab)) {
                    // Editing title...
                    return;
                }

                if (!self.props.locked) {
                    // Only process the arrow down if unlocked (this stops dragging when locked)
                    defaultFDown(e);
                }

                if (
                    tab.contentItem.parent.getActiveContentItem() !==
                    tab.contentItem
                ) {
                    // Change the active tab...
                    tab.contentItem.parent.setActiveContentItem(
                        tab.contentItem
                    );
                }
            });
    };

    private handleResize = () => {
        const { width, height } = this.props;
        this.gl!.updateSize(width, height);
    };

    public componentDidMount() {
        const config = this.configFromProps();
        this.initLayout(config);
    }

    public componentWillUnmount() {
        if (this.gl) {
            this.gl.destroy();
            this.gl = undefined;
        }
    }

    private configFromProps(): LayoutRendererConfig {
        const { layoutShape, services } = this.props;

        return configFromProps(this.store, services, layoutShape);
    }

    public render() {
        const { locked } = this.props;
        return (
            <div
                ref={(node) => (this.ref = node!)}
                className={classnames('LayoutRenderer', locked && 'locked')}
            />
        );
    }

    private onDraggingWidget(added: {
        id: WidgetId;
        config?: AnyConfig | undefined;
    }) {
        const { id, config } = added;

        this.onAddingComponent(config, id.widgetType);
        this.props.onDraggingWidget(added);
    }

    private onAddingComponent(config: AnyConfig | undefined, type: WidgetType) {
        if (type === WidgetType.TILES) {
            makeCapsuleIdsUnique(
                config as CapsuleContainerWidgetConfigState,
                this.widgetsService
            );
        }
    }
}

export default connect(
    selectLayoutRenderer,
    layoutRendererDispatcher
)(LayoutRenderer);
